import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { getWeek } from 'date-fns';
import {
  Select,
  SelectItem,
  SelectSection,
  Checkbox,
  Skeleton,
  Card,
  CardBody,
  Divider,
  Button,
  DatePicker,
  Link,
  Tabs,
  Tab,
  Tooltip,
} from '@nextui-org/react';
import { MARKET_LABELS, MARKETS, DEFAULT_ORIGIN } from '../../utils/definitions';
import useExchangeRates from '../../hooks/useExchangeRates';
import { Icon } from '@iconify/react';
import VarietyPriceChart from '../../components/charts/VarietyPriceChart';
import DailyMarketChart from '../../components/charts/DailyMarketChart';
import { exporterBrandKey } from '../../components/charts/exporterSeries';
import type { BitacoraMode } from '../../components/charts/bitacoraSeries';
import { parseDate } from '@internationalized/date';
import ModalSlideFilter from '../../components/filters/modal-sidebar-filter';
import 'rsuite/dist/rsuite.min.css';
import { DateRangePicker } from 'rsuite';
import { MarketData } from 'common/src/utils/types';
import useMarketData from 'common/src/hooks/useMarketData';
import useMarketDateSync from '../../hooks/useMarketDateSync';
import InformationTooltip from '../../components/ui/InformationTooltip';
import {
  Alert,
  EmptyState,
  FiltersToggle,
  PageHeader,
  PageLayout,
} from '../../components/ui';
import { logUserAction } from '../../../../admin/src/services/actionsService';
import { Selection } from '@react-types/shared';
import useDataStore from '../../stores/useDataStore';
import UnitPriceFilter from 'common/src/components/filters/UnitPriceFilter';
import useUserStore from '../../stores/useUserStore';
import useFilterStore from '../../stores/useFiltersStore';
import PriceLevelFilter from 'common/src/components/filters/PriceLevelFilter';
import MarketCommissionsModal from '../../modals/MarketCommissionsModal';
import {
  loadMarketDataCommissions,
  saveMarketDataCommissions,
  MarketDataCommissions,
  loadFavoriteExporters,
  loadFavoriteBrands,
} from 'common/src/api/presettlement-settings';
import { toast } from 'react-toastify';
// Read session directly from store to avoid duplicating auth subscriptions
import { useTranslation } from '../../hooks/useTranslation';
import RegisterPricesModal from '../../components/market/RegisterPricesModal';
import {
  listBitacoraPrices,
  BitacoraPriceData,
} from 'common/src/api/market_comments';

interface MarketTodayProps {
  forceSpeciesId?: number; // Force a specific species id (e.g., 2 for cherries in importers app)
  hideExporterPrice?: boolean; // Hide exporter price points in charts (blue/green dots)
  hideMaxExporters?: boolean; // Hide "Exportadoras Más Registradas" selector
}

const MarketToday = ({
  forceSpeciesId,
  hideExporterPrice = false,
  hideMaxExporters = false,
}: MarketTodayProps) => {
  const { t } = useTranslation('market');
  const { session } = useUserStore();
  const { selectedSpecies, priceLevel } = useDataStore();
  const [isFilterOpen, setIsFilterOpen] = useState(
    // En pantallas chicas los filtros son un panel que tapa la página: se
    // arranca cerrado para que "Registrar precios" quede a mano.
    () =>
      typeof window === 'undefined' ||
      window.matchMedia('(min-width: 768px)').matches
  );
  const { userProfile } = useUserStore();
  const { selectedSeason } = useFilterStore();

  // Use forceSpeciesId if provided, otherwise use selectedSpecies from store
  const effectiveSpeciesId = forceSpeciesId ?? selectedSpecies?.id;

  const [viewMode, setViewMode] = useState<'day' | 'week'>('day');
  const [selectedWeek, setSelectedWeek] = useState(1);
  const [visualization, setVisualization] = useState<'chart' | 'table'>(
    'chart'
  );

  const [selectedFormato, setSelectedFormato] = useState('all');
  const [selectedColor, setSelectedColor] = useState('all');
  const [selectedCurrency, setSelectedCurrency] = useState<'RMB' | 'USD'>(
    'RMB'
  );

  const isCherry = effectiveSpeciesId === 2;

  // Force weekly view for non-cherry species
  useEffect(() => {
    if (!isCherry) {
      setViewMode('week');
    }
  }, [isCherry]);

  // Force USD when priceLevel is not 'market', reset to RMB when back to 'market'
  useEffect(() => {
    if (priceLevel !== 'market') {
      setSelectedCurrency('USD');
    } else {
      setSelectedCurrency('RMB');
    }
  }, [priceLevel]);
  const [selectedMarket, setSelectedMarket] = useState('all');
  // Por defecto Chile: es lo que mira el 90% de los usuarios. La fruta del
  // hemisferio norte (USA/Canadá) convive en el dataset y deforma la lectura.
  // Si la especie no tiene data chilena, el hook de fechas re-ancla solo.
  const [selectedOrigins, setSelectedOrigins] = useState<string[]>([DEFAULT_ORIGIN]);
  const [priceView, setPriceView] = useState<string>('kg');
  const [boxSizes, setBoxSizes] = useState<number[]>([]);
  const [selectedExporters, setSelectedExporters] = useState<string[]>([]);
  const [selectedTransport, setSelectedTransport] = useState('all');
  const [isCommissionsModalOpen, setIsCommissionsModalOpen] = useState(false);
  const [commissionsSettings, setCommissionsSettings] =
    useState<MarketDataCommissions | null>(null);
  const [commissionsReloadTrigger, setCommissionsReloadTrigger] = useState(0);
  const [favoriteExporters, setFavoriteExporters] = useState<string[]>([]);
  const [loadingFavorites, setLoadingFavorites] = useState(true);

  // Brand separation states
  const [separationType, setSeparationType] = useState<'none' | 'brand'>(
    'none'
  );
  const [selectedBrands, setSelectedBrands] = useState<string[]>([]);
  // Etiquetas de las exportadoras seleccionadas: parten todas marcadas, así
  // que se guardan las desmarcadas (las que aparecen al cambiar la fecha
  // entran marcadas).
  const [separateExporterBrands, setSeparateExporterBrands] = useState(false);
  const [deselectedExporterBrands, setDeselectedExporterBrands] = useState<
    string[]
  >([]);
  const [favoriteBrands, setFavoriteBrands] = useState<string[]>([]);
  const [loadingFavoriteBrands, setLoadingFavoriteBrands] = useState(true);
  const [isRegisterPricesOpen, setIsRegisterPricesOpen] = useState(false);
  // Puntos de Bitácora: un promedio por calibre, o uno por cliente o por etiqueta.
  const [bitacoraMode, setBitacoraMode] = useState<BitacoraMode>('average');
  const [bitacoraData, setBitacoraData] = useState<BitacoraPriceData[]>([]);
  const [bitacoraReloadTrigger, setBitacoraReloadTrigger] = useState(0);

  // Ref para controlar la inicialización de exportadoras seleccionadas
  const initializedExporters = useRef(false);
  // Ref para controlar la inicialización de etiquetas seleccionadas
  const initializedBrands = useRef(false);

  const hasDemo = userProfile?.exporter?.hasDemo;
  const DEMO_MIN_DATE_STR = '2024-12-15';
  const DEMO_MAX_DATE_STR = '2025-01-01';

  // ✅ NUEVO: Hook centralizado que maneja todas las fechas y elimina competing useEffects
  const {
    selectedDate,
    dateRange,
    isLoading: loadingSelectedDate,
    updateSelectedDate,
    updateDateRange,
    setSelectedDate,
    setDateRange,
  } = useMarketDateSync({
    speciesId: effectiveSpeciesId,
    season: selectedSeason,
    hasDemo,
    viewMode,
    demoDefaultDate: '2024-12-20',
    daysBack: 7,
    origins: selectedOrigins, // ancla el rango a la última fecha con data de esos orígenes
  });

  // Sync selectedWeek from dateRange for non-cherry species
  useEffect(() => {
    if (!isCherry && dateRange && dateRange[0]) {
      const weekNum = getWeek(dateRange[0], { weekStartsOn: 1, firstWeekContainsDate: 4 });
      setSelectedWeek(weekNum);
    }
  }, [isCherry, dateRange?.[0]?.getTime()]);

  const { marketData, loading } = useMarketData({
    viewMode,
    selectedDate,
    dateRange,
    enabled: selectedDate !== '',
    reloadTrigger: commissionsReloadTrigger,
    forceSpeciesId,
  });

  // Exchange rates for dynamic currency conversion
  const {
    rate: exchangeRate,
    rateInfo: exchangeRateInfo,
    isDefault: isDefaultRate,
    getRateForDate,
  } = useExchangeRates({
    date: viewMode === 'day' ? selectedDate : undefined,
    dateRange: viewMode !== 'day' ? dateRange : undefined,
    currency: 'RMB',
    enabled: selectedDate !== '',
  });

  // Dynamic RMB to USD conversion using exchange rate from DB
  const rmbToUsd = useCallback(
    (rmb: number): number => {
      return rmb / exchangeRate;
    },
    [exchangeRate]
  );

  // Per-date RMB to USD conversion (always uses the specific date's rate)
  const rmbToUsdForDate = useCallback(
    (rmb: number, dateStr: string): number => {
      const rate = getRateForDate(dateStr);
      return rmb / rate;
    },
    [getRateForDate]
  );

  // Load bitácora prices for chart overlay
  useEffect(() => {
    let cancelled = false;
    async function loadBitacoraPrices() {
      try {
        const params: any = {};
        if (viewMode === 'day' && selectedDate) {
          params.date = selectedDate;
        } else if (viewMode !== 'day' && dateRange) {
          const [from, to] = dateRange;
          params.from = from.toISOString().split('T')[0];
          params.to = to.toISOString().split('T')[0];
        }
        if (effectiveSpeciesId) params.species_id = effectiveSpeciesId;
        if (selectedSeason) params.season = selectedSeason;
        if (selectedMarket && selectedMarket !== 'all')
          params.market_id = selectedMarket;
        const data = await listBitacoraPrices(params);
        if (!cancelled) setBitacoraData(data);
      } catch {
        if (!cancelled) setBitacoraData([]);
      }
    }
    loadBitacoraPrices();
    return () => {
      cancelled = true;
    };
  }, [
    viewMode,
    selectedDate,
    dateRange?.[0]?.toISOString?.(),
    dateRange?.[1]?.toISOString?.(),
    effectiveSpeciesId,
    selectedSeason,
    selectedMarket,
    bitacoraReloadTrigger, // Reload when bitácora is edited
  ]);

  useEffect(() => {
    logUserAction('view', 'page', 'daily-market');
  }, []);

  // Load commission settings when species changes
  useEffect(() => {
    const loadSettings = async () => {
      if (!selectedSpecies?.id || !userProfile?.exporter?.id || !session)
        return;

      const settings = await loadMarketDataCommissions(
        userProfile.exporter.id,
        selectedSpecies.id,
        session
      );
      setCommissionsSettings(settings);
    };

    loadSettings();
  }, [selectedSpecies?.id, userProfile?.exporter?.id]);

  // Load favorite exporters when species or season changes
  useEffect(() => {
    const loadFavorites = async () => {
      if (
        !userProfile?.exporter?.id ||
        !selectedSpecies?.id ||
        !selectedSeason ||
        !session
      ) {
        setLoadingFavorites(false);
        return;
      }

      setLoadingFavorites(true);
      // Resetear la bandera de inicialización cuando cambien especie/temporada
      initializedExporters.current = false;
      const favorites = await loadFavoriteExporters(
        userProfile.exporter.id,
        selectedSeason,
        selectedSpecies.id,
        session
      );
      setFavoriteExporters(favorites.values);
      setLoadingFavorites(false);
    };

    loadFavorites();
  }, [userProfile?.exporter?.id, selectedSpecies?.id, selectedSeason, session]);

  // Load favorite brands when species or season changes
  useEffect(() => {
    const loadFavBrands = async () => {
      if (
        !userProfile?.exporter?.id ||
        !selectedSpecies?.id ||
        !selectedSeason ||
        !session
      ) {
        setLoadingFavoriteBrands(false);
        return;
      }

      setLoadingFavoriteBrands(true);
      // Resetear la bandera de inicialización cuando cambien especie/temporada
      initializedBrands.current = false;
      const favBrands = await loadFavoriteBrands(
        userProfile.exporter.id,
        selectedSeason,
        selectedSpecies.id,
        session
      );
      setFavoriteBrands(favBrands.values);
      setLoadingFavoriteBrands(false);
    };

    loadFavBrands();
  }, [userProfile?.exporter?.id, selectedSpecies?.id, selectedSeason, session]);

  // Check if we need to open modal when price level changes
  const handlePriceLevelChange = useCallback(
    (newLevel: typeof priceLevel) => {
      if (newLevel !== 'market' && !commissionsSettings) {
        // Need to configure settings first
        setIsCommissionsModalOpen(true);
      }
    },
    [commissionsSettings]
  );

  // Save commission settings
  const handleSaveCommissions = async (
    commissions: MarketDataCommissions
  ): Promise<boolean> => {
    if (!userProfile?.exporter?.id || !selectedSpecies?.id || !session) {
      toast.error('Error: Datos de usuario o especie no disponibles');
      return false;
    }

    const success = await saveMarketDataCommissions(
      userProfile.exporter.id,
      selectedSpecies.id,
      commissions,
      session
    );

    if (success) {
      setCommissionsSettings(commissions);
      // Trigger reload of commissions in hook
      setCommissionsReloadTrigger((prev) => prev + 1);
      toast.success('Configuración guardada exitosamente');
      return true;
    } else {
      toast.error('Error al guardar la configuración');
      return false;
    }
  };

  // ✅ Handle species default_unit_price (mantener este effect, no compite con fechas)
  useEffect(() => {
    if (selectedSpecies?.id === undefined) return;

    // Set the price view based on default_unit_price if available
    if (selectedSpecies?.default_unit_price) {
      const defaultUnitPrice = selectedSpecies.default_unit_price;
      setPriceView(defaultUnitPrice === 1 ? 'kg' : `box-${defaultUnitPrice}`);

      // Set boxSizes to ONLY contain the default unit price
      if (defaultUnitPrice > 1) {
        setBoxSizes([defaultUnitPrice]);
      } else {
        setBoxSizes([]); // No box sizes if default is kg
      }
    } else {
      // If no default_unit_price, reset to kg
      setPriceView('kg');
      setBoxSizes([]);
    }
  }, [selectedSpecies?.id, selectedSpecies?.default_unit_price]);

  const handleDateRangeChange = useCallback(
    (value: [Date, Date] | null) => {
      if (!value) return;
      let [start, end] = value;

      // Clamp to demo range if applicable
      if (hasDemo) {
        const minDate = new Date(`${DEMO_MIN_DATE_STR}T12:00:00.000Z`);
        const maxDate = new Date(`${DEMO_MAX_DATE_STR}T12:00:00.000Z`);
        if (start < minDate) start = new Date(minDate);
        if (start > maxDate) start = new Date(maxDate);
        if (end < minDate) end = new Date(minDate);
        if (end > maxDate) end = new Date(maxDate);
        if (end < start) end = new Date(start);
      }

      // Update date range using hook function
      start.setHours(12, 0, 0, 0);
      end.setHours(12, 0, 0, 0);
      updateDateRange([start, end]);

      const weekNumber = Math.ceil(
        (start.getTime() - new Date(start.getFullYear(), 0, 1).getTime()) /
        (7 * 24 * 60 * 60 * 1000)
      );
      setSelectedWeek(weekNumber);
    },
    [updateDateRange, hasDemo, DEMO_MIN_DATE_STR, DEMO_MAX_DATE_STR]
  );

  // ✅ OPTIMIZACIÓN: Memoizar función de filtrado
  // Always uses per-date exchange rate, never the average
  const filterData = useCallback(
    (
      data: MarketData[],
      formato: string,
      currency: 'RMB' | 'USD'
    ): MarketData[] => {
      const validPrices = data.filter(
        (item) => item.price_kg !== null && item.price_kg > 0
      );

      const filtered = validPrices.filter(
        (item) =>
          (formato === 'all' || item.weight_kg === formato) &&
          (selectedColor === 'all' ||
            (selectedColor === 'Dark' && item?.color?.endsWith('D')) ||
            (selectedColor === 'Light' && !item?.color?.endsWith('D'))) &&
          (selectedMarket === 'all' || item.region === selectedMarket) &&
          (selectedOrigins.length === 0 ||
            (!!item.origin && selectedOrigins.includes(item.origin))) &&
          (selectedTransport === 'all' ||
            (selectedTransport === 'OCEAN' && item.transport === 'OCEAN') ||
            (selectedTransport === 'AIR' && item.transport === 'AIR'))
      );

      // Get multiplier from priceView value (either 1 for 'kg' or the number after 'box-')
      const multiplier =
        priceView === 'kg' ? 1 : parseInt(priceView.split('-')[1]);

      return filtered.map((item: MarketData) => {
        let price = item.price_kg;

        // Only apply currency conversion if in 'market' mode
        // (FOB/Producer prices already come in USD from the hook)
        if (priceLevel === 'market') {
          // Market prices come in RMB, convert to USD if needed
          // Always use per-date conversion with the item's specific date
          if (currency === 'USD') {
            price = rmbToUsdForDate(price, item.date);
          }
          // If currency is RMB, keep as is
        }
        // If priceLevel is not 'market', price is already in USD from hook

        // Apply box multiplier
        price *= multiplier;

        return {
          ...item,
          price_kg: price,
        };
      });
    },
    [
      selectedColor,
      selectedMarket,
      selectedOrigins,
      selectedTransport,
      priceView,
      priceLevel,
      rmbToUsdForDate,
    ]
  );

  // ✅ OPTIMIZACIÓN: Calcular filtered data con useMemo en lugar de useState + useEffect
  const filteredData = useMemo(
    () => filterData(marketData, selectedFormato, selectedCurrency),
    [marketData, selectedFormato, selectedCurrency, filterData]
  );

  // ✅ Transform bitácora data with same price conversion as market data
  // Always uses per-date exchange rate, never the average
  const transformedBitacoraData = useMemo(() => {
    if (bitacoraData.length === 0) return [];

    // Get multiplier from priceView value (either 1 for 'kg' or the number after 'box-')
    const multiplier =
      priceView === 'kg' ? 1 : parseInt(priceView.split('-')[1]);

    return bitacoraData.map((item) => {
      const itemDate = item.date_from; // Use the bitácora entry date for exchange rate lookup
      // unit_price / unit_weight en la moneda en que se registró. Lo que sigue
      // asume RMB, así que un registro en dólares se pasa a RMB primero.
      let price =
        item.local_currency === 'USD'
          ? item.price_kg * getRateForDate(itemDate)
          : item.price_kg;

      // Apply same price level calculation as market data
      if (priceLevel === 'market') {
        // Market mode: just convert currency if needed
        if (selectedCurrency === 'USD') {
          // Always use per-date conversion
          price = rmbToUsdForDate(price, itemDate);
        }
      } else if (commissionsSettings) {
        // FOB or Producer mode: apply commission calculations
        // First convert to USD using the specific date's rate
        let priceUsdPerKg = rmbToUsdForDate(price, itemDate);

        // Use fleet if specified, otherwise default to ocean (M = Maritimo)
        const isAir = item.fleet === 'A';
        const freightCost = isAir
          ? commissionsSettings.air_freight_cost
          : commissionsSettings.ocean_freight_cost;
        const otherCosts = isAir
          ? commissionsSettings.air_other_costs
          : commissionsSettings.ocean_other_costs;

        // Calculate FOB: Price - Receiver Commission - VAT - Freight - Other Costs
        const receiverCommission =
          priceUsdPerKg * (commissionsSettings.receiver_commission / 100);
        const vat = priceUsdPerKg * (commissionsSettings.vat_tax / 100);
        const fobPrice =
          priceUsdPerKg - receiverCommission - vat - freightCost - otherCosts;

        if (priceLevel === 'fob') {
          price = Math.max(0, fobPrice);
        } else {
          // Producer level
          const exporterCommission =
            fobPrice * (commissionsSettings.exporter_commission / 100);
          const producerReturn =
            fobPrice - exporterCommission - commissionsSettings.packing_cost;
          price = Math.max(0, producerReturn);
        }
      }

      // Apply box multiplier
      price *= multiplier;

      return {
        ...item,
        price_kg: price,
      };
    });
  }, [bitacoraData, priceView, selectedCurrency, priceLevel, commissionsSettings, rmbToUsdForDate, getRateForDate]);

  // ✅ OPTIMIZACIÓN: Calcular top exporters (solo depende de filteredData)
  const topExporters = useMemo(() => {
    // Calculate top exporters
    const exporterCounts = filteredData.reduce(
      (acc, item) => {
        if (
          item.exporter &&
          item.exporter !== 'PRICE RANGO' &&
          item.exporter !== 'SIN EXPORTADORA'
        ) {
          acc[item.exporter] = (acc[item.exporter] || 0) + 1;
        }
        return acc;
      },
      {} as Record<string, number>
    );

    return Object.entries(exporterCounts)
      .map(([id, count]) => ({ id, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10); // Limitar a top 10
  }, [filteredData]);

  // ✅ Determinar qué exportadoras mostrar en el selector
  const exportersToShow = useMemo(() => {
    // Si tiene favoritos configurados, SOLO mostrar esos
    if (favoriteExporters.length > 0) {
      // Calcular conteos para los favoritos
      const exporterCounts = filteredData.reduce(
        (acc, item) => {
          if (item.exporter && favoriteExporters.includes(item.exporter)) {
            acc[item.exporter] = (acc[item.exporter] || 0) + 1;
          }
          return acc;
        },
        {} as Record<string, number>
      );

      // Retornar favoritos con sus conteos (ordenados por conteo)
      return favoriteExporters
        .map((id) => ({ id, count: exporterCounts[id] || 0 }))
        .sort((a, b) => b.count - a.count);
    }

    // Si NO tiene favoritos, mostrar el top 10
    return topExporters;
  }, [favoriteExporters, filteredData, topExporters]);

  // ✅ OPTIMIZACIÓN: Calcular topExportersData (depende de filteredData y selectedExporters)
  const topExportersData = useMemo(() => {
    return filteredData.filter(
      (item) =>
        item.exporter && selectedExporters.some((exp) => exp === item.exporter)
    );
  }, [filteredData, selectedExporters]);

  // Etiquetas de cada exportadora seleccionada (sin la propia), por registros.
  const exporterBrandGroups = useMemo(() => {
    const ownExporter = userProfile?.exporter?.excel_name;
    const counts: Record<string, Record<string, number>> = {};
    topExportersData.forEach((item) => {
      if (!item.exporter || item.exporter === ownExporter) return;
      const brand = item.brand || '';
      if (!counts[item.exporter]) counts[item.exporter] = {};
      counts[item.exporter][brand] = (counts[item.exporter][brand] || 0) + 1;
    });
    return selectedExporters
      .filter((exporter) => counts[exporter])
      .map((exporter) => ({
        exporter,
        brands: Object.entries(counts[exporter])
          .sort((a, b) => b[1] - a[1])
          .map(([brand, count]) => ({
            key: exporterBrandKey(exporter, brand),
            brand,
            count,
          })),
      }));
  }, [topExportersData, selectedExporters, userProfile?.exporter?.excel_name]);

  const allExporterBrandKeys = useMemo(
    () =>
      exporterBrandGroups.flatMap((group) => group.brands.map((b) => b.key)),
    [exporterBrandGroups]
  );

  const selectedExporterBrands = useMemo(
    () =>
      allExporterBrandKeys.filter(
        (key) => !deselectedExporterBrands.includes(key)
      ),
    [allExporterBrandKeys, deselectedExporterBrands]
  );

  const handleExporterBrandsChange = (keys: Selection) => {
    const keysArray = Array.from(keys) as string[];
    if (keysArray.includes('all')) {
      setDeselectedExporterBrands(
        selectedExporterBrands.length > 0 ? allExporterBrandKeys : []
      );
      return;
    }
    // Las que no están a la vista (otra fecha) mantienen su estado.
    setDeselectedExporterBrands((prev) => [
      ...prev.filter((key) => !allExporterBrandKeys.includes(key)),
      ...allExporterBrandKeys.filter((key) => !keysArray.includes(key)),
    ]);
  };

  // ✅ Calcular brands disponibles del usuario con conteo de registros
  const { userBrands, topUserBrands } = useMemo(() => {
    if (!userProfile?.exporter?.excel_name)
      return { userBrands: [], topUserBrands: [] };

    const brandCounts: Record<string, number> = {};
    filteredData.forEach((item) => {
      if (item.exporter === userProfile.exporter?.excel_name && item.brand) {
        brandCounts[item.brand] = (brandCounts[item.brand] || 0) + 1;
      }
    });

    const brands = Object.keys(brandCounts).sort();
    const topBrands = Object.entries(brandCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([brand]) => brand);

    return { userBrands: brands, topUserBrands: topBrands };
  }, [filteredData, userProfile?.exporter?.excel_name]);

  // ✅ Actualizar selectedExporters basado en favoritos o topExporters
  useEffect(() => {
    // Si tiene exportadoras favoritas configuradas
    if (favoriteExporters.length > 0) {
      // Solo inicializar una vez cuando hay favoritas
      if (!initializedExporters.current) {
        setSelectedExporters(favoriteExporters);
        initializedExporters.current = true;
      }
      // Si ya se inicializó con favoritas, no hacer nada (mantener selección del usuario)
    }
    // Si NO tiene favoritas, auto-seleccionar el top 10 actual (se actualiza con cada cambio de fecha)
    else if (topExporters.length > 0) {
      const newTopExporters = topExporters.map((exp) => exp.id);

      // Solo actualizar si el top 10 realmente cambió (evitar re-renders innecesarios)
      setSelectedExporters((prev) => {
        // Comparar si los arrays son diferentes
        if (prev.length !== newTopExporters.length) return newTopExporters;

        const hasDifference = newTopExporters.some(
          (exp, idx) => exp !== prev[idx]
        );
        return hasDifference ? newTopExporters : prev;
      });
    }
  }, [topExporters, favoriteExporters]);

  // ✅ Auto-seleccionar etiquetas favoritas o top 5 cuando se carguen (solo si separationType === 'brand')
  useEffect(() => {
    // Solo cuando estamos en modo brand y no hemos inicializado
    if (separationType !== 'brand' || initializedBrands.current) return;

    // Si tiene etiquetas favoritas configuradas, seleccionarlas
    if (favoriteBrands.length > 0 && userBrands.length > 0) {
      // Filtrar solo las favoritas que existen en los datos actuales
      const validFavorites = favoriteBrands.filter((brand) =>
        userBrands.includes(brand)
      );
      if (validFavorites.length > 0) {
        setSelectedBrands(validFavorites);
        initializedBrands.current = true;
      }
    }
    // Si no tiene etiquetas favoritas, auto-seleccionar las top 5
    else if (topUserBrands.length > 0) {
      setSelectedBrands(topUserBrands);
      initializedBrands.current = true;
    }
  }, [separationType, favoriteBrands, userBrands, topUserBrands]);

  // ✅ Resetear selectedBrands cuando cambie el modo de separación
  useEffect(() => {
    if (separationType === 'none') {
      setSelectedBrands([]);
      initializedBrands.current = false;
    }
  }, [separationType]);

  // ✅ OPTIMIZACIÓN: Memoizar listas computadas
  const COLORS = ['all', 'Dark', 'Light'] as const;

  const formats = useMemo(
    () => ['all', ...new Set(marketData.map((item) => item.weight_kg))],
    [marketData]
  );

  // Se incluye lo seleccionado además de lo que trae el rango actual: si no,
  // al filtrar por CHILE en una ventana sin fruta chilena la opción elegida
  // desaparecería de la lista y no habría forma de volver atrás.
  const origins = useMemo(() => {
    const found = new Set<string>();
    for (const item of marketData) {
      if (item.origin) found.add(item.origin);
    }
    for (const origin of selectedOrigins) found.add(origin);
    return Array.from(found).sort((a, b) => a.localeCompare(b));
  }, [marketData, selectedOrigins]);

  const handleViewModeChange = useCallback((value: 'day' | 'week') => {
    setViewMode(value);
    // No necesitamos hacer nada más, el hook ya maneja las fechas correctamente
  }, []);

  const handlePreviousDay = useCallback(() => {
    const currentDate = new Date(selectedDate);
    currentDate.setDate(currentDate.getDate() - 1);
    let newDateString = currentDate.toISOString().split('T')[0];

    // Clamp to demo range if applicable
    if (hasDemo && newDateString < DEMO_MIN_DATE_STR) {
      newDateString = DEMO_MIN_DATE_STR;
    }

    updateSelectedDate(newDateString);
  }, [selectedDate, hasDemo, DEMO_MIN_DATE_STR, updateSelectedDate]);

  const handleNextDay = useCallback(() => {
    const currentDate = new Date(selectedDate);
    currentDate.setDate(currentDate.getDate() + 1);
    let newDateString = currentDate.toISOString().split('T')[0];

    // Clamp to demo range if applicable
    if (hasDemo && newDateString > DEMO_MAX_DATE_STR) {
      newDateString = DEMO_MAX_DATE_STR;
    }

    updateSelectedDate(newDateString);
  }, [selectedDate, hasDemo, DEMO_MAX_DATE_STR, updateSelectedDate]);

  const handleExporterSelection = (keys: Selection) => {
    // Convert Selection type to array of strings
    const keysArray = Array.from(keys) as string[];

    if (keysArray.includes('all')) {
      // If 'all' is selected and there were previous selections, clear all
      if (selectedExporters.length > 0) {
        setSelectedExporters([]);
      } else {
        // Otherwise, select all exporters from the current list (favorites or top 10)
        setSelectedExporters(exportersToShow.map((exp) => exp.id));
      }
    } else {
      // Normal selection behavior for individual items
      setSelectedExporters(keysArray);
    }
  };

  return (
    <div className='flex w-full h-screen overflow-x-hidden'>
      {/* Sticky Sidebar */}
      <ModalSlideFilter
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
      >
        <div className='bg-background p-4 pt-8 h-full overflow-y-auto'>
          <div className='space-y-4'>
            {hasDemo && (
              <Alert className='mb-4'>
                {t('Contrata CherryMarket para habilitar todas las opciones.')}
              </Alert>
            )}
            <p className='text-sm text-default-500 mb-1'>{t('Período')}</p>
            <div>
              {isCherry && (
              <Tabs
                fullWidth
                size='sm'
                aria-label={t('Período')}
                className='mb-2'
                selectedKey={viewMode}
                disabledKeys={hasDemo ? ['week'] : []}
                onSelectionChange={(k) =>
                  handleViewModeChange(k as 'day' | 'week')
                }
              >
                <Tab key='day' title={t('Por día')} />
                <Tab key='week' title={t('Por rango')} />
              </Tabs>
              )}

              {loadingSelectedDate ? (
                <Skeleton className='w-full h-10' />
              ) : viewMode === 'day' ? (
                <div className='space-y-2'>
                  <DatePicker
                    label={t('Fecha')}
                    value={parseDate(selectedDate || '')}
                    minValue={
                      hasDemo ? parseDate(DEMO_MIN_DATE_STR) : undefined
                    }
                    maxValue={
                      hasDemo ? parseDate(DEMO_MAX_DATE_STR) : undefined
                    }
                    onChange={(e) => {
                      const dateString = e?.toString() || '';
                      // Clamp within demo range if needed
                      if (hasDemo) {
                        const clamped = dateString
                          ? dateString < DEMO_MIN_DATE_STR
                            ? DEMO_MIN_DATE_STR
                            : dateString > DEMO_MAX_DATE_STR
                              ? DEMO_MAX_DATE_STR
                              : dateString
                          : '';
                        updateSelectedDate(clamped);
                      } else {
                        updateSelectedDate(dateString);
                      }
                    }}
                    className='w-full'
                  />
                  <div className='flex gap-2'>
                    <Button
                      isDisabled={
                        hasDemo &&
                        (!!selectedDate
                          ? selectedDate <= DEMO_MIN_DATE_STR
                          : true)
                      }
                      isIconOnly
                      className='flex-1 bg-default-100'
                      onClick={handlePreviousDay}
                    >
                      <Icon icon='tabler:chevron-left' />
                    </Button>
                    <Button
                      isDisabled={
                        hasDemo &&
                        (!!selectedDate
                          ? selectedDate >= DEMO_MAX_DATE_STR
                          : true)
                      }
                      className='flex-1 bg-default-100'
                      onClick={handleNextDay}
                      isIconOnly
                    >
                      <Icon icon='tabler:chevron-right' />
                    </Button>
                  </div>
                </div>
              ) : !isCherry ? (
                <div>
                  <DateRangePicker
                    value={dateRange}
                    onChange={handleDateRangeChange}
                    hoverRange='week'
                    showWeekNumbers
                    showOneCalendar
                    placeholder={t('Seleccionar semana')}
                    character=' - '
                    format='dd-MM-yyyy'
                    className='w-full'
                    menuStyle={{ zIndex: 9999 }}
                    isoWeek={true}
                    renderValue={(value) => {
                      if (!value || !value[0] || !value[1]) return '';
                      const startW = getWeek(value[0], { weekStartsOn: 1, firstWeekContainsDate: 4 });
                      const endW = getWeek(value[1], { weekStartsOn: 1, firstWeekContainsDate: 4 });
                      return startW === endW
                        ? `${t('Semana')} ${startW}`
                        : `${t('Semana')} ${startW} - ${t('Semana')} ${endW}`;
                    }}
                    locale={{
                      sunday: 'Do',
                      monday: 'Lu',
                      tuesday: 'Ma',
                      wednesday: 'Mi',
                      thursday: 'Ju',
                      friday: 'Vi',
                      saturday: 'Sa',
                      ok: t('Aplicar'),
                      today: t('Hoy'),
                      yesterday: t('Ayer'),
                      last7Days: t('Últimos 7 días'),
                    }}
                  />
                </div>
              ) : (
                <div>
                  <DateRangePicker
                    value={dateRange}
                    onChange={handleDateRangeChange}
                    showWeekNumbers
                    showOneCalendar
                    placeholder={t('Seleccionar rango de fechas')}
                    character=' - '
                    format='dd-MM-yyyy'
                    className='w-full'
                    menuStyle={{ zIndex: 9999 }}
                    isoWeek={true}
                    ranges={[
                      {
                        label: t('Esta semana'),
                        value: [
                          new Date('2024-02-13T12:00:00'),
                          new Date('2024-02-19T12:00:00'),
                        ],
                      },
                    ]}
                    locale={{
                      sunday: 'Do',
                      monday: 'Lu',
                      tuesday: 'Ma',
                      wednesday: 'Mi',
                      thursday: 'Ju',
                      friday: 'Vi',
                      saturday: 'Sa',
                      ok: t('Aplicar'),
                      today: t('Hoy'),
                      yesterday: t('Ayer'),
                      last7Days: t('Últimos 7 días'),
                    }}
                  />
                </div>
              )}
            </div>

            <Divider />
            <p className='text-sm text-default-500 mb-1'>{t('Visualización')}</p>
            <Tabs
              fullWidth
              size='sm'
              aria-label={t('Tipo de visualización')}
              selectedKey={visualization}
              onSelectionChange={(k) => setVisualization(k as 'chart' | 'table')}
            >
              <Tab key='chart' title={t('Gráficos')} />
              <Tab key='table' title={t('Tablas')} />
            </Tabs>
            <div className='space-y-1'>
              <PriceLevelFilter
                className='w-full'
                onLevelChange={handlePriceLevelChange}
              />
              {priceLevel !== 'market' && (
                <button
                  onClick={() => setIsCommissionsModalOpen(true)}
                  className='text-xs text-default-600 hover:text-default-700 underline'
                >
                  {t('Editar configuración')}
                </button>
              )}
            </div>

            {!hideMaxExporters && (
              <>
                {/* Mi exportadora: separar o no mis etiquetas, y cuáles */}
                <Divider />
                <div className='space-y-2'>
                  <div className='flex items-center gap-1'>
                    <p className='text-sm text-default-500'>
                      {t('Mi exportadora')}
                    </p>
                    <Tooltip content={t('Configura tus etiquetas a seguir')}>
                      <Button
                        as={Link}
                        href='/settings'
                        isIconOnly
                        size='sm'
                        variant='light'
                        className='h-6 w-6 min-w-6'
                        aria-label={t('Configura tus etiquetas a seguir')}
                      >
                        <Icon icon='tabler:settings' className='text-base text-default-500' />
                      </Button>
                    </Tooltip>
                  </div>
                  <Checkbox
                    size='sm'
                    isSelected={separationType === 'brand'}
                    onValueChange={(checked) =>
                      setSeparationType(checked ? 'brand' : 'none')
                    }
                    isDisabled={hasDemo || userBrands.length === 0}
                  >
                    {t('Separar por etiqueta')}
                  </Checkbox>
                  {separationType === 'brand' && (
                    <>
                      <Select
                        isDisabled={hasDemo || loadingFavoriteBrands}
                        label={t('Etiquetas')}
                        selectionMode='multiple'
                        selectedKeys={selectedBrands}
                        onSelectionChange={(keys) => {
                          const keysArray = Array.from(keys) as string[];
                          if (keysArray.includes('all')) {
                            // Toggle all
                            if (selectedBrands.length > 0) {
                              setSelectedBrands([]);
                            } else {
                              setSelectedBrands(userBrands);
                            }
                          } else {
                            setSelectedBrands(keysArray);
                          }
                        }}
                        className='w-full'
                      >
                        <SelectItem key='all' value='all'>
                          <div className='flex justify-between items-center'>
                            <span>
                              {selectedBrands.length > 0
                                ? t('Deseleccionar todas')
                                : t('Seleccionar todas')}
                            </span>
                          </div>
                        </SelectItem>
                        {
                          userBrands.map((brand) => (
                            <SelectItem key={brand} value={brand} textValue={brand}>
                              {brand}
                            </SelectItem>
                          )) as any
                        }
                      </Select>
                    </>
                  )}
                </div>
                {/* Exportadoras a seguir: cuáles, separarlas o no, y qué etiquetas */}
                <Divider />
                <div className='space-y-2'>
                  <div className='flex items-center gap-1'>
                    <p className='text-sm text-default-500'>
                      {t('Exportadoras a seguir')}
                    </p>
                    <Tooltip content={t('Configura tus exportadoras a seguir')}>
                      <Button
                        as={Link}
                        href='/settings'
                        isIconOnly
                        size='sm'
                        variant='light'
                        className='h-6 w-6 min-w-6'
                        aria-label={t('Configura tus exportadoras a seguir')}
                      >
                        <Icon icon='tabler:settings' className='text-base text-default-500' />
                      </Button>
                    </Tooltip>
                  </div>
                  <Select
                    isDisabled={hasDemo || loadingFavorites}
                    label={t('Exportadoras seleccionadas')}
                    selectionMode='multiple'
                    selectedKeys={selectedExporters}
                    onSelectionChange={handleExporterSelection as any}
                    className='w-full'
                  >
                    <SelectItem key='all' value='all'>
                      <div className='flex justify-between items-center'>
                        <span>
                          {selectedExporters.length > 0
                            ? t('Deseleccionar todos')
                            : t('Seleccionar todos')}
                        </span>
                      </div>
                    </SelectItem>
                    {
                      exportersToShow.map((exporter) => (
                        <SelectItem
                          key={exporter.id}
                          value={exporter.id}
                          textValue={exporter.id}
                        >
                          <div className='flex justify-between items-center'>
                            <span>{exporter.id}</span>
                            <span className='text-small text-default-400'>
                              ({exporter.count} {t('registros')})
                            </span>
                          </div>
                        </SelectItem>
                      )) as any
                    }
                  </Select>
                  <Checkbox
                    size='sm'
                    isSelected={separateExporterBrands}
                    onValueChange={setSeparateExporterBrands}
                    isDisabled={hasDemo || exporterBrandGroups.length === 0}
                  >
                    {t('Separar por etiqueta')}
                  </Checkbox>
                  {separateExporterBrands && (
                    <Select
                      isDisabled={hasDemo}
                      label={t('Etiquetas')}
                      selectionMode='multiple'
                      selectedKeys={selectedExporterBrands}
                      onSelectionChange={handleExporterBrandsChange}
                      className='w-full'
                    >
                      {[
                        <SelectItem key='all' value='all' textValue={t('Todas')}>
                          {selectedExporterBrands.length > 0
                            ? t('Deseleccionar todas')
                            : t('Seleccionar todas')}
                        </SelectItem>,
                        ...exporterBrandGroups.map((group) => (
                          <SelectSection
                            key={group.exporter}
                            title={group.exporter}
                            showDivider
                          >
                            {group.brands.map((b) => (
                              <SelectItem
                                key={b.key}
                                value={b.key}
                                textValue={`${group.exporter} · ${b.brand || t('Sin etiqueta')}`}
                              >
                                <div className='flex justify-between items-center'>
                                  <span>{b.brand || t('Sin etiqueta')}</span>
                                  <span className='text-small text-default-400'>
                                    ({b.count} {t('registros')})
                                  </span>
                                </div>
                              </SelectItem>
                            ))}
                          </SelectSection>
                        )),
                      ]}
                    </Select>
                  )}
                </div>
              </>
            )}

            {/* <Divider /> */}
            <Divider />
            <p className='text-sm text-default-500'>{t('Bitácora')}</p>
            <Select
              label={t('Puntos de Bitácora')}
              selectedKeys={[bitacoraMode]}
              onSelectionChange={(keys) =>
                setBitacoraMode(
                  (Array.from(keys)[0] as BitacoraMode) || 'average'
                )
              }
              className='w-full'
              isDisabled={hasDemo}
            >
              <SelectItem key='average' value='average'>
                {t('Promedio')}
              </SelectItem>
              <SelectItem key='client' value='client'>
                {t('Por cliente')}
              </SelectItem>
              <SelectItem key='label' value='label'>
                {t('Por etiqueta')}
              </SelectItem>
            </Select>

            <p className='text-sm text-default-500 mb-1'>{t('Filtros')}</p>
            {selectedSpecies?.has_color && (
              <Select
                label={t('Color')}
                value={selectedColor}
                onChange={(e) => setSelectedColor(e.target.value)}
                defaultSelectedKeys={[selectedColor]}
                className='w-full'
              >
                {COLORS.map((color) => (
                  <SelectItem key={color} value={color}>
                    {color === 'all' ? t('Todos') : color}
                  </SelectItem>
                ))}
              </Select>
            )}
            <Select
              label={t('Formato Caja')}
              value={selectedFormato}
              onChange={(e) => setSelectedFormato(e.target.value)}
              defaultSelectedKeys={['all']}
              className='w-full'
            >
              {formats.map((format) => (
                <SelectItem key={format} value={format}>
                  {format === 'all' ? t('Todos') : format}
                </SelectItem>
              ))}
            </Select>
            <Select
              label={t('Mercado')}
              value={selectedMarket}
              onChange={(e) => setSelectedMarket(e.target.value)}
              defaultSelectedKeys={['all']}
              className='w-full'
            >
              {MARKETS.map((market) => (
                <SelectItem
                  key={market}
                  value={market}
                  textValue={MARKET_LABELS[market]}
                >
                  {MARKET_LABELS[market]}
                </SelectItem>
              ))}
            </Select>
            <Select
              label={t('Origen')}
              selectionMode='multiple'
              selectedKeys={new Set(selectedOrigins)}
              onSelectionChange={(keys) => {
                const selected = Array.from(keys) as string[];
                if (selected.includes('all')) {
                  setSelectedOrigins(
                    selectedOrigins.length > 0 ? [] : (origins as string[])
                  );
                  return;
                }
                setSelectedOrigins(selected);
              }}
              className='w-full'
            >
              {[
                {
                  key: 'all',
                  label:
                    selectedOrigins.length > 0
                      ? t('Deseleccionar todos')
                      : t('Seleccionar todos'),
                },
                ...origins.map((origin) => ({
                  key: String(origin),
                  label: String(origin),
                })),
              ].map((option) => (
                <SelectItem key={option.key} value={option.key}>
                  {option.label}
                </SelectItem>
              ))}
            </Select>
            <Select
              label={t('Transporte')}
              value={selectedTransport}
              onChange={(e) => setSelectedTransport(e.target.value)}
              defaultSelectedKeys={['all']}
              className='w-full'
            >
              <SelectItem key='all' value='all'>
                {t('Todos')}
              </SelectItem>
              <SelectItem key='OCEAN' value='OCEAN'>
                {t('Marítimo')}
              </SelectItem>
              <SelectItem key='AIR' value='AIR'>
                {t('Aéreo')}
              </SelectItem>
            </Select>

            <Divider />
            <p className='text-sm text-default-500 mb-1'>{t('Unidades')}</p>
            <Select
              label={t('Moneda')}
              value={selectedCurrency}
              onChange={(e) =>
                setSelectedCurrency(e.target.value as 'RMB' | 'USD')
              }
              defaultSelectedKeys={['RMB']}
              className='w-full'
              isDisabled={priceLevel !== 'market'}
              description={
                priceLevel !== 'market'
                  ? t('Los cálculos FOB/Productor se muestran en USD')
                  : undefined
              }
            >
              <SelectItem key='RMB' value='RMB' textValue='RMB ¥'>
                RMB ¥
              </SelectItem>
              <SelectItem key='USD' value='USD' textValue='USD $'>
                USD $
              </SelectItem>
            </Select>
            {selectedCurrency === 'USD' && (
              <div className='text-xs text-default-400 -mt-2 px-1 flex items-center gap-1'>
                <span className='font-medium'>{t('Tipo de cambio')}:</span>{' '}
                <span className={isDefaultRate ? 'text-amber-600' : 'text-green-600'}>
                  {exchangeRateInfo}
                </span>
                <InformationTooltip size={14}>
                  <p className='text-sm font-semibold mb-1'>
                    {t('Conversión USD/RMB')}
                  </p>
                  <p className='text-xs'>
                    {viewMode === 'day'
                      ? t('Se aplica el tipo de cambio del día seleccionado.')
                      : t('A cada día del rango se le aplica su tipo de cambio correspondiente. Solo se muestra el promedio como referencia.')}
                  </p>
                </InformationTooltip>
              </div>
            )}
            <UnitPriceFilter
              selectedSpecies={selectedSpecies}
              value={priceView}
              onChange={setPriceView}
              className='w-full'
              label={t('Unidad de precio')}
            />
          </div>
        </div>
      </ModalSlideFilter>

      <PageLayout>
        <PageHeader
          title={t('Mercado')}
          description={t('Analiza los precios del mercado en tiempo real.')}
          titleAddon={
            <InformationTooltip>
              <p className='text-sm font-semibold mb-2'>
                {t('Vista General del Mercado')}
              </p>
              <ul className='list-disc ml-4 mt-1 text-sm space-y-2'>
                <li>
                  <span className='font-medium'>{t('Análisis Temporal:')}</span>{' '}
                  {t(
                    'Visualiza datos por día específico o rango de fechas para análisis comparativo'
                  )}
                </li>
                <li>
                  <span className='font-medium'>{t('Gráficos:')}</span>
                  <ul className='list-circle ml-4 mt-1 space-y-1'>
                    <li>
                      {t(
                        'Box Plot: Distribución de precios (min, Q1, mediana, Q3, max)'
                      )}
                    </li>
                    <li>{t('Tendencias por calibre y variedad')}</li>
                  </ul>
                </li>
                <li>
                  <span className='font-medium'>
                    {t('Filtros Disponibles:')}
                  </span>
                  <ul className='list-circle ml-4 mt-1 space-y-1'>
                    <li>{t('Color: Dark/Light')}</li>
                    <li>{t('Formato de caja y peso')}</li>
                    <li>{t('Mercado específico')}</li>
                    <li>{t('Moneda (RMB/USD)')}</li>
                    <li>{t('Unidad (kg/caja)')}</li>
                  </ul>
                </li>
                <li>
                  <span className='font-medium'>{t('Comparativa:')}</span>{' '}
                  {t(
                    'Selecciona hasta 10 exportadores para comparar su posicionamiento en el mercado'
                  )}
                </li>
              </ul>
            </InformationTooltip>
          }
        />
        {hasDemo && (
          <Alert className='mb-4'>
            {t(
              'Estás viendo una versión de prueba con datos limitados. Contrata CherryMarket para cargar todos los datos y fechas.'
            )}
          </Alert>
        )}
        {/* <Divider className='my-4' /> */}
        <div className='flex justify-between mb-4'>
          <FiltersToggle
            isOpen={isFilterOpen}
            onToggle={() => setIsFilterOpen(!isFilterOpen)}
            label={isFilterOpen ? t('Ocultar filtros') : t('Mostrar filtros')}
          />
          <Button
            size='sm'
            color='primary'
            onPress={() => setIsRegisterPricesOpen(true)}
            isDisabled={hasDemo}
          >
            {t('Registrar precios')}
          </Button>
        </div>
        {loading ? (
          <div className='space-y-10'>
            <Card className='w-full'>
              <CardBody className='p-4'>
                <Skeleton className='rounded-lg w-full'>
                  <div className='h-8 w-1/4 rounded-lg bg-default-200'></div>
                  <div className='h-80 w-full mt-4 rounded-lg bg-default-300'></div>
                </Skeleton>
              </CardBody>
            </Card>
            <Card className='w-full'>
              <CardBody className='p-4'>
                <Skeleton className='rounded-lg w-full'>
                  <div className='h-8 w-1/3 rounded-lg bg-default-200'></div>
                  <div className='h-80 w-full mt-4 rounded-lg bg-default-300'></div>
                </Skeleton>
              </CardBody>
            </Card>
          </div>
        ) : marketData.length === 0 ? (
          <EmptyState
            title={t('No hay datos disponibles')}
            description={
              <>
                {t('No se encontraron registros para')}{' '}
                {String(selectedSpecies?.id) === '0'
                  ? t('ninguna especie')
                  : t('la especie seleccionada')}{' '}
                {t('en la fecha o rango seleccionado.')}
              </>
            }
          />
        ) : (
          <>
            <>
              {/* overflow-visible: el hover de los puntos de Bitácora puede ser
                  más alto que el espacio sobre el punto, y la Card de NextUI
                  (overflow-hidden) lo recortaba. */}
              <Card className='p-4 mb-8 overflow-visible'>
                <DailyMarketChart
                  data={filteredData}
                  selectedCurrency={selectedCurrency}
                  priceView={priceView.startsWith('box') ? 'box' : 'kg'}
                  topExportersData={hideExporterPrice ? [] : topExportersData}
                  boxSize={
                    priceView.startsWith('box')
                      ? parseInt(priceView.split('-')[1])
                      : 5
                  }
                  visualization={visualization}
                  hideExporterLegend={hideExporterPrice}
                  separationType={separationType}
                  selectedBrands={selectedBrands}
                  bitacoraData={transformedBitacoraData}
                  bitacoraMode={bitacoraMode}
                  exporterBrandKeys={
                    separateExporterBrands ? selectedExporterBrands : null
                  }
                />
              </Card>
              <Card className='p-4 overflow-visible'>
                <VarietyPriceChart
                  data={filteredData}
                  currency={selectedCurrency}
                  priceView={priceView.startsWith('box') ? 'box' : 'kg'}
                  topExportersData={hideExporterPrice ? [] : topExportersData}
                  boxSize={
                    priceView.startsWith('box')
                      ? parseInt(priceView.split('-')[1])
                      : 5
                  }
                  maxVarieties={7}
                  visualization={visualization}
                  hideExporterLegend={hideExporterPrice}
                  separationType={separationType}
                  selectedBrands={selectedBrands}
                  bitacoraData={transformedBitacoraData}
                  bitacoraMode={bitacoraMode}
                  exporterBrandKeys={
                    separateExporterBrands ? selectedExporterBrands : null
                  }
                />
              </Card>
            </>
          </>
        )}
      </PageLayout>

      {/* Registro rápido de precios de venta (Bitácora) */}
      <RegisterPricesModal
        isOpen={isRegisterPricesOpen}
        onClose={() => setIsRegisterPricesOpen(false)}
        onSaved={() => setBitacoraReloadTrigger((n) => n + 1)}
        exporterId={userProfile?.exporter?.id}
        userId={userProfile?.id}
        userName={[userProfile?.first_name, userProfile?.last_name]
          .filter(Boolean)
          .join(' ')}
        speciesId={effectiveSpeciesId}
        season={selectedSeason || undefined}
        varieties={selectedSpecies?.varieties || []}
        calibers={selectedSpecies?.calibers || []}
        defaultMarket={selectedMarket !== 'all' ? selectedMarket : undefined}
      />

      {/* Modal de configuración de comisiones */}
      <MarketCommissionsModal
        isOpen={isCommissionsModalOpen}
        onClose={() => setIsCommissionsModalOpen(false)}
        onSave={handleSaveCommissions}
        initialValues={commissionsSettings || undefined}
      />
    </div>
  );
};

export default MarketToday;
